import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, RefreshCw } from "lucide-react";

const STATUS = { 200: "entregado", 201: "entregado", 403: "rechazado (clave VAPID)", 404: "suscripción vencida", 410: "suscripción vencida", 0: "error" };
const ago = (iso) => {
  if (!iso) return "nunca";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `hace ${m} min` : m < 1440 ? `hace ${Math.round(m / 60)} h` : `hace ${Math.round(m / 1440)} días`;
};
const fmt = (iso) => new Date(iso).toLocaleString("es-CL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const service = (h) => (h.includes("apple") ? "iPhone" : h.includes("google") ? "Android/Chrome" : h.includes("mozilla") ? "Firefox" : h);

function Status({ code, error }) {
  const ok = code === 200 || code === 201;
  return <span title={error || ""} className={`text-[11px] rounded-full px-2 py-0.5 ${ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{STATUS[code] || `código ${code}`}</span>;
}

export function PushTab({ call }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call("/cal/admin/push-status").then(setSt).catch((e) => toast.error(e.message)), [call]);
  useEffect(() => { load(); }, [load]);

  const test = async () => {
    setBusy(true);
    try {
      const r = await call("/cal/admin/test-push");
      if (!r.results.length) toast.error("No tienes avisos activados en ningún aparato. Actívalos en Mi perfil.");
      else if (r.results.some((x) => x.ok)) toast.success("¡Aviso enviado! Debería llegarte en segundos");
      else toast.error(`No se pudo entregar: ${r.results.map((x) => x.error || STATUS[x.status] || x.status).join(", ")}`, { duration: 15000 });
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!st) return <div className="h-40 rounded-3xl bg-cream animate-pulse" />;
  return (
    <div className="space-y-5" data-testid="cal-admin-push-tab">
      <div className="flex flex-wrap gap-2">
        <button data-testid="cal-admin-test-push" onClick={test} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-night text-white px-5 py-2.5 text-sm font-semibold cal-press disabled:opacity-50">
          <BellRing className="w-4 h-4" /> {busy ? "Enviando…" : "Mándame un aviso de prueba"}
        </button>
        <button onClick={load} aria-label="Actualizar" data-testid="cal-admin-push-refresh" className="p-2.5 rounded-full border border-line"><RefreshCw className="w-4 h-4 text-cocoa" /></button>
      </div>
      <p data-testid="cal-admin-cron-last" className="text-sm text-cocoa-soft">Último chequeo del cron: <b className="text-cocoa">{ago(st.cron_last?.at)}</b> (revisa cada hora, a las 9:00 de cada uno manda los avisos)</p>
      <section>
        <h3 className="text-xs uppercase tracking-[0.2em] text-amber-deep font-semibold mb-2">Aparatos con avisos ({st.subs.length})</h3>
        <div className="rounded-3xl bg-cream border border-line divide-y divide-line/70">
          {st.subs.map((s) => (
            <div key={s.hash} className="px-4 py-2.5 flex items-center justify-between gap-2" data-testid={`cal-admin-sub-${s.hash}`}>
              <div className="min-w-0"><p className="text-sm text-cocoa truncate">{s.name}</p><p className="text-[11px] text-cocoa-soft">{service(s.service)} · {s.tz}</p></div>
              {!s.encrypted && <span className="text-[10px] text-cocoa-soft italic">reactivar pa' aviso directo</span>}
            </div>
          ))}
          {!st.subs.length && <p className="p-4 text-sm italic text-cocoa-soft">Nadie ha activado los avisos todavía.</p>}
        </div>
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-[0.2em] text-amber-deep font-semibold mb-2">Últimos envíos de las 9:00</h3>
        <div className="space-y-2">
          {st.cronlog.map((l, i) => (
            <div key={i} className="rounded-2xl bg-cream border border-line px-4 py-3" data-testid={`cal-admin-cronlog-${i}`}>
              <p className="text-sm font-semibold text-cocoa">{fmt(l.at)} · {l.sent} enviados{l.failed ? ` · ${l.failed} fallidos` : ""}{l.skipped_no_events ? ` · ${l.skipped_no_events} sin novedades` : ""}</p>
              {l.deliveries.map((d, j) => (
                <div key={j} className="mt-1">
                  <p className="text-xs text-cocoa-soft flex items-center gap-2"><Status code={d.status} error={d.error} /> {d.name} · {d.title}</p>
                  {d.error && <p data-testid={`cal-admin-cronlog-error-${i}-${j}`} className="text-[11px] text-red-800 break-all mt-0.5">{d.error}</p>}
                </div>
              ))}
            </div>
          ))}
          {!st.cronlog.length && <p className="text-sm italic text-cocoa-soft">Todavía no hay envíos registrados.</p>}
        </div>
      </section>
    </div>
  );
}
